import { useEffect, useRef, useState } from 'react';
import Raven from 'raven-js';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { Flex } from 'foundations-components/components/Flex';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { Box } from 'foundations-components/transitional/components/Box';
import { Link } from 'foundations-components/transitional/components/Link';
import { Text } from 'foundations-components/components/Text';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import ConnectAppModal from '../ConnectApp/ConnectAppModal';
import { DATA_SYNC_OPTIONS, ONE_CLICK_SYNC_APPS } from '../../constants/dataSyncConstants';
import DataSyncTile from './DataSyncTile';
import { getMarketPlaceOpsTeamsUrl, redirectTo, wrapUrlWithParams } from '../../utils/urls';
import { getIsDataSyncOption, waitFor } from '../../utils/connectAppUtils';
import { showSyncSuccessAlert, showSyncFailureAlert } from '../../utils/connectAppUtils';
import { DATA_SYNC_BUTTON_OVERRIDE } from '../../constants/dataSyncButtonOverride';
import { trackPageview, trackUserInteraction } from '../../utils/tracking';
import { useDataSyncOptions } from '../../hooks/useDataSyncOptions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DataSyncOptionsModal = ({
  beforeRedirect,
  headingSubtitle,
  hostApp,
  rootNode,
  withMarketplaceRedirectParams,
  withSyncRedirectParams,
  onClose = () => {},
  onCloseComplete = () => {},
  onOpenStart = () => {},
  onOpen = () => {}
}) => {
  const [chosenConnectApp, setChosenConnectApp] = useState();
  const chosenConnectAppRef = useRef(chosenConnectApp);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const {
    dataSyncOptions = DATA_SYNC_OPTIONS
  } = useDataSyncOptions();
  useEffect(() => {
    chosenConnectAppRef.current = chosenConnectApp;
  }, [chosenConnectApp]);
  const handleOneClickSyncSuccess = () => {
    showSyncSuccessAlert();
    if (chosenConnectApp) {
      trackUserInteraction({
        action: 'one click sync complete',
        value: chosenConnectApp,
        app: hostApp
      });
    }
  };
  const handleOneClickSyncFailure = error => {
    showSyncFailureAlert(error.type);
    // The OAuth window closing is almost certainly from user
    // choice, and we treat it as a cancel action.
    if (error.type === 'OAuthWindowClosed' && chosenConnectApp) {
      return trackUserInteraction({
        action: 'one click sync cancel',
        value: chosenConnectApp,
        app: hostApp
      });
    }
    Raven.captureException(new Error(`Auth Error ${error.type} occurred`), {
      extra: {
        authError: error,
        hostApp
      }
    });
  };
  const handleOneClickConnectionFailure = (error, accountId, appId) => {
    showSyncFailureAlert();
    Raven.captureException(error, {
      extra: {
        accountId,
        appId,
        hostApp
      }
    });
  };
  if (chosenConnectApp && !ONE_CLICK_SYNC_APPS.includes(chosenConnectApp)) {
    return /*#__PURE__*/_jsx(ConnectAppModal, {
      app: chosenConnectApp,
      rootNode: rootNode,
      onCancel: () => {
        trackUserInteraction({
          action: 'sync cancel',
          value: chosenConnectApp,
          app: hostApp
        });
        setChosenConnectApp(undefined);
      },
      onOpen: () => {
        trackPageview({
          screen: 'data sync modal',
          subscreen: 'connect app',
          app: hostApp
        });
      },
      onClose: () => {
        trackUserInteraction({
          action: 'close sync setup',
          value: chosenConnectApp,
          app: hostApp
        });
        setChosenConnectApp(undefined);
      },
      onCloseComplete: () => {
        // Do not trigger onCloseComplete when back to the main modal
        if (chosenConnectAppRef.current) {
          onCloseComplete();
        }
      },
      onSyncDoThisLaterClick: optionApp => {
        trackUserInteraction({
          action: 'sync do this later click',
          value: optionApp,
          app: hostApp
        });
      },
      onSyncBeginSetupClick: optionApp => {
        trackUserInteraction({
          action: 'sync begin setup click',
          value: optionApp,
          app: hostApp
        });
      },
      onConnectionComplete: optionApp => {
        trackUserInteraction({
          action: 'setup connection complete',
          value: optionApp,
          app: hostApp
        });
      }
    });
  }
  return /*#__PURE__*/_jsxs(Modal, {
    "data-test-id": "data-sync-modal",
    onOpenStart: onOpenStart,
    onOpenComplete: () => {
      trackPageview({
        screen: 'data sync modal',
        subscreen: 'index',
        app: hostApp
      });
      onOpen();
    },
    onCloseComplete: () => {
      // Do not trigger onCloseComplete when going to the connect app modal
      const connectApp = chosenConnectAppRef.current;
      if (!connectApp || ONE_CLICK_SYNC_APPS.includes(connectApp)) {
        onCloseComplete();
      }
    },
    rootNode: rootNode,
    use: "conversational",
    width: 780,
    children: [/*#__PURE__*/_jsx(DialogCloseButton, {
      "data-test-id": "data-sync-modal-close-button",
      onClick: () => {
        trackUserInteraction({
          action: 'close data sync modal',
          app: hostApp
        });
        onClose();
      }
    }), /*#__PURE__*/_jsxs(Box, {
      padding: "900",
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h4",
        textAlign: "center",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "growthDataModal.header.title"
        })
      }), /*#__PURE__*/_jsx(Text, {
        textAlign: "center",
        children: headingSubtitle
      })]
    }), /*#__PURE__*/_jsx(DialogBody, {
      "data-test-id": "data-sync-buttons",
      children: /*#__PURE__*/_jsx(Flex, {
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        flexDirection: "row",
        children: Object.entries(dataSyncOptions).map(([key, {
          url
        }]) => {
          const optionApp = key;
          const dataSyncRedirect = url && wrapUrlWithParams(url, withSyncRedirectParams);
          const ButtonComponent = DATA_SYNC_BUTTON_OVERRIDE[key];
          return /*#__PURE__*/_jsx(Box, {
            basis: "25%",
            marginInlineEnd: "400",
            marginBlockEnd: "400",
            grow: 0,
            shrink: 0,
            children: /*#__PURE__*/_jsx(DataSyncTile, {
              app: optionApp,
              ButtonComponent: ButtonComponent,
              href: dataSyncRedirect,
              rootNode: rootNode,
              onOneClickAuthStatusChange: isAuthenticating => setShowLoadingOverlay(isAuthenticating),
              onOneClickAuthSuccess: handleOneClickSyncSuccess,
              onOneClickAuthError: handleOneClickSyncFailure,
              onOneClickConnectionError: handleOneClickConnectionFailure,
              onClick: event => {
                trackUserInteraction({
                  action: 'click data sync tile',
                  value: optionApp,
                  app: hostApp
                });
                if (getIsDataSyncOption(optionApp)) {
                  return setChosenConnectApp(optionApp);
                }

                // Intended for network requests that need to be made before
                // potentially redirecting to other pages
                if (beforeRedirect && dataSyncRedirect) {
                  event.preventDefault();
                  Promise.race([beforeRedirect(), waitFor(2000)])
                  // No-op
                  .catch().finally(() => {
                    redirectTo(dataSyncRedirect);
                  });
                }
              }
            })
          }, key);
        })
      })
    }), /*#__PURE__*/_jsx(DialogFooter, {
      align: "center",
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        elements: {
          Link
        },
        message: "growthDataModal.footer_jsx",
        options: {
          'data-test-id': 'data-sync-modal-footer',
          external: true,
          href: wrapUrlWithParams(getMarketPlaceOpsTeamsUrl(), withMarketplaceRedirectParams),
          onClick: () => {
            trackUserInteraction({
              action: 'click marketplace link',
              app: hostApp
            });
          }
        }
      })
    }), showLoadingOverlay && /*#__PURE__*/_jsx(LoadingOverlay, {})]
  });
};
export default DataSyncOptionsModal;